// utils/sanitizeHtml.js
//
// Sanitizers for any string that reaches dangerouslySetInnerHTML / innerHTML (UI-1).
// Kept free of other imports so components and tests can use it without pulling
// in marked/turndown.

import DOMPurify from "dompurify";

// Private instance with default settings. Mermaid ("strict") registers hooks on the
// shared default DOMPurify that keep <a target>; app sanitization must not depend on
// whether a diagram has rendered. Hooks and config are per-instance in DOMPurify.
const purifier = DOMPurify(window);

// target only survives when a call opts in (allowNewTabLinks); then keep "_blank"
// with rel forced against reverse tabnabbing, and drop any other target value.
purifier.addHook("afterSanitizeAttributes", (node) => {
  if (!node.hasAttribute("target")) return;
  if (node.getAttribute("target").toLowerCase() === "_blank") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  } else {
    node.removeAttribute("target");
  }
});

// Sanitize an HTML fragment (JD bodies, toaster/alert messages). Strips <script>,
// event handlers (onerror=…) and javascript: URLs; keeps formatting tags such as
// <strong>, <span>, <br>, <p>, lists and links. allowNewTabLinks keeps
// target="_blank" on app-authored messages whose links must not leave the page
// (e.g. the Google Safety Check link shown next to an unsaved form).
export const sanitizeHtml = (html, { allowNewTabLinks = false } = {}) => {
  if (html === null || html === undefined || html === "") return "";
  return purifier.sanitize(String(html), allowNewTabLinks ? { ADD_ATTR: ["target"] } : {});
};

// Sanitize an SVG string produced by mermaid.render(). Mermaid draws node labels
// as HTML inside <foreignObject>, which DOMPurify drops by default (labels would
// disappear), so foreignObject is allowed as an HTML integration point. Its HTML
// contents are still sanitized like any other fragment. dominant-baseline is a
// keyword-only presentation attribute mermaid uses to centre text (sequence, C4,
// architecture, journey, timeline, quadrant); DOMPurify's SVG allowlist lacks it.
export const sanitizeSvg = (svg) => {
  if (!svg) return "";
  return purifier.sanitize(String(svg), {
    ADD_TAGS: ["foreignObject"],
    ADD_ATTR: ["dominant-baseline"],
    HTML_INTEGRATION_POINTS: { foreignobject: true },
  });
};
